import { Link } from 'react-router-dom';
import { Package, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useCartContext } from '../../hooks/useCart';
import { useOrders } from '../../hooks/useOrders';

const TERMINAL_STATES = new Set(['CANCELLED', 'REFUNDED', 'DISPUTED', 'DELIVERED', 'COMPLETED']);

interface ShopperNavActionsProps {
  /** Tailwind classes for icon buttons (padding/size). */
  className?: string;
  iconClassName?: string;
}

/**
 * Bag + Track Orders actions shown in shopper headers.
 * Track Orders only appears for signed-in shoppers.
 */
export function ShopperNavActions({
  className = 'p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors',
  iconClassName = 'h-5 w-5 md:h-6 md:w-6 text-heading',
}: ShopperNavActionsProps) {
  const { user } = useAuth();
  const cart = useCartContext();
  const { orders } = useOrders(user?.uid || '', 'shopper');

  const activeOrderCount = user
    ? orders.filter((o) => !TERMINAL_STATES.has(o.state)).length
    : 0;

  return (
    <div className="flex items-center space-x-1 md:space-x-2">
      {user && (
        <Link
          to="/orders"
          className={`relative ${className}`}
          aria-label="Track orders"
          title="Track orders"
        >
          <Package className={iconClassName} />
          {activeOrderCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 md:h-5 md:w-5 flex items-center justify-center bg-primary text-white text-[10px] md:text-xs font-bold rounded-full">
              {activeOrderCount > 9 ? '9+' : activeOrderCount}
            </span>
          )}
        </Link>
      )}
      <Link to="/bag" className={`relative ${className}`} aria-label="Shopping bag">
        <ShoppingBag className={iconClassName} />
        {cart.itemCount > 0 && (
          <span className="absolute -top-1 -right-1 h-4 w-4 md:h-5 md:w-5 flex items-center justify-center bg-primary text-white text-[10px] md:text-xs font-bold rounded-full">
            {cart.itemCount}
          </span>
        )}
      </Link>
    </div>
  );
}
